"use client"
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Crop, Loader, Maximize2, X } from "lucide-react"

import { CROP_BACKGROUND, loadImage, readFileAsDataURL, renderCropBox } from "../../utils/imageResize"

const OUTPUT_SIZE = 512
const HANDLES = ["nw", "ne", "se", "sw"]
/** How much of the (square) stage the image spans — the rest is room to grab. */
const MARGIN = 0.8

/**
 * Frame the avatar image before it is saved.
 *
 * The image used to be stored at its original aspect ratio and then dropped
 * into a square with `object-cover`, which silently centre-crops it: a wide
 * photo lost its sides, a portrait lost the head. Here the user sees the whole
 * image and draws the square themselves — drag the box, pull a corner — and
 * what they cut is what gets written. Every surface downstream draws a square,
 * so nothing crops again.
 *
 * The box lives in *source-image pixels*, not screen pixels, so it means the
 * same thing on a phone and on a 27" display and survives a resize. It may
 * stick out past the image (that is what "fit whole" is), and the overhang is
 * filled with a blurred copy of the image rather than dead bars.
 */
export default function ImageCropper({ file, onCancel, onDone, onError }) {
  const [image, setImage] = useState(null)
  const [box, setBox] = useState(null)
  const [stage, setStage] = useState({ w: 0, h: 0 })
  const [saving, setSaving] = useState(false)

  const stageRef = useRef(null)
  const dragRef = useRef(null)

  // Held in a ref so the loader below depends on the file alone. Callers pass
  // inline arrows, and re-running this on every parent render would throw away
  // the framing the user was in the middle of.
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  useEffect(() => {
    let cancelled = false
    readFileAsDataURL(file)
      .then(loadImage)
      .then((img) => {
        if (cancelled) return
        setImage(img)
        setBox(fillBox(img))
      })
      .catch((err) => !cancelled && onErrorRef.current?.(err?.message || "Failed to read image"))
    return () => {
      cancelled = true
    }
  }, [file])

  // The stage is fluid (it shrinks on a phone); the maths below needs its real px.
  useLayoutEffect(() => {
    const el = stageRef.current
    if (!el) return undefined
    const measure = () => {
      const r = el.getBoundingClientRect()
      setStage({ w: r.width, h: r.height })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [image])

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCancel()
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [onCancel])

  // The image is shown whole, centred: one scale and one origin convert between
  // image pixels and the pixels on screen.
  //
  // The stage is square and the image only takes MARGIN of it, because the box
  // is allowed to reach past the image — at full size it is a square around the
  // longest side — and both it and its corner handles have to stay grabbable.
  // Fit the image edge-to-edge and they end up clipped by the stage.
  const view = image && stage.w
    ? (() => {
        const scale = (Math.min(stage.w, stage.h) * MARGIN) / Math.max(image.width, image.height)
        return {
          scale,
          left: (stage.w - image.width * scale) / 2,
          top: (stage.h - image.height * scale) / 2,
        }
      })()
    : null

  const clampBox = useCallback(
    (next) => {
      if (!image) return next
      const min = Math.min(image.width, image.height) * 0.1
      // Growing past the whole image only buys more blurred padding, and it is
      // what would push the box off the stage.
      const max = Math.max(image.width, image.height)
      const size = Math.min(max, Math.max(min, next.size))
      // The box may hang off the image, but its centre has to stay on it —
      // otherwise you can drag the crop into empty space and lose the picture.
      return {
        size,
        x: Math.min(image.width - size / 2, Math.max(-size / 2, next.x)),
        y: Math.min(image.height - size / 2, Math.max(-size / 2, next.y)),
      }
    },
    [image]
  )

  const onPointerDown = (e, mode) => {
    if (!image || !view) return
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = { mode, x: e.clientX, y: e.clientY, from: box }
  }

  const onPointerMove = (e) => {
    const drag = dragRef.current
    if (!drag || !view) return
    // Screen delta -> image-pixel delta.
    const dx = (e.clientX - drag.x) / view.scale
    const dy = (e.clientY - drag.y) / view.scale
    const b = drag.from

    if (drag.mode === "move") {
      setBox(clampBox({ ...b, x: b.x + dx, y: b.y + dy }))
      return
    }

    // Resizing keeps the box square and pins the opposite corner, so the corner
    // you are not holding does not creep.
    const right = b.x + b.size
    const bottom = b.y + b.size
    let size = b.size
    if (drag.mode === "se") size = b.size + (dx + dy) / 2
    if (drag.mode === "nw") size = b.size - (dx + dy) / 2
    if (drag.mode === "ne") size = b.size + (dx - dy) / 2
    if (drag.mode === "sw") size = b.size + (dy - dx) / 2

    const anchored = {
      size,
      x: drag.mode === "nw" || drag.mode === "sw" ? right - size : b.x,
      y: drag.mode === "nw" || drag.mode === "ne" ? bottom - size : b.y,
    }
    setBox(clampBox(anchored))
  }

  const endDrag = () => {
    dragRef.current = null
  }

  const handleUse = () => {
    if (!image || !box) return
    setSaving(true)
    try {
      onDone(renderCropBox(image, box, { size: OUTPUT_SIZE }))
    } catch (err) {
      onErrorRef.current?.(err?.message || "Failed to process image")
      setSaving(false)
    }
  }

  const isFill = image && box && near(box.size, Math.min(image.width, image.height))
  const isFit = image && box && near(box.size, Math.max(image.width, image.height))
  const overhangs =
    image && box && (box.x < 0 || box.y < 0 || box.x + box.size > image.width || box.y + box.size > image.height)

  // Screen rect of the crop box.
  const rect = view && box
    ? {
        left: view.left + box.x * view.scale,
        top: view.top + box.y * view.scale,
        size: box.size * view.scale,
      }
    : null

  const content = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-[460px] flex-col rounded-2xl border border-white/20 bg-[#0e0e13] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-white">Adjust image</p>
            <p className="text-[11px] text-gray-400">Drag the box, pull a corner to resize</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cancel"
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4">
          <div
            ref={stageRef}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{ background: CROP_BACKGROUND, touchAction: "none" }}
            className="relative aspect-square w-full select-none overflow-hidden rounded-xl border border-white/15"
          >
            {image && view ? (
              <>
                {/* What renderCropBox bakes in behind an overhanging box. Shown
                    here too, or the preview would be lying about the result. */}
                <img
                  src={image.src}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  style={{ filter: "blur(16px)", transform: "scale(1.2)", opacity: 0.35 }}
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />
                <img
                  src={image.src}
                  alt=""
                  draggable={false}
                  style={{
                    left: view.left,
                    top: view.top,
                    width: image.width * view.scale,
                    height: image.height * view.scale,
                  }}
                  className="pointer-events-none absolute max-w-none"
                />

                {/* The crop box. Everything outside it is dimmed by one huge
                    shadow rather than four overlay divs. */}
                <div
                  onPointerDown={(e) => onPointerDown(e, "move")}
                  style={{
                    left: rect.left,
                    top: rect.top,
                    width: rect.size,
                    height: rect.size,
                    boxShadow: "0 0 0 9999px rgba(6, 6, 10, 0.66)",
                  }}
                  className="absolute cursor-move border-2 border-white/90"
                >
                  {HANDLES.map((h) => (
                    <span
                      key={h}
                      onPointerDown={(e) => onPointerDown(e, h)}
                      style={cornerStyle(h)}
                      className="absolute h-6 w-6 rounded-full border-2 border-white bg-primary shadow"
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Loader className="h-5 w-5 animate-spin text-gray-500" />
              </div>
            )}
          </div>

          {/* Two framings worth one click. Everything else is the box itself. */}
          <div className="mt-4 flex gap-2">
            <PresetButton
              active={isFill}
              disabled={!image}
              onClick={() => setBox(fillBox(image))}
              Icon={Crop}
              label="Fill frame"
            />
            <PresetButton
              active={isFit}
              disabled={!image}
              onClick={() => setBox(fitBox(image))}
              Icon={Maximize2}
              label="Fit whole"
            />
          </div>

          {overhangs && (
            <p className="mt-3 text-[11px] leading-snug text-gray-500">
              The box reaches past the image, so those edges are filled with a blurred
              copy of it.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-white/20 px-3 py-2 text-sm text-gray-300 transition-colors hover:bg-white/10 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUse}
            disabled={!image || saving}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white transition-colors hover:bg-purple-500 disabled:opacity-50"
          >
            {saving && <Loader className="h-4 w-4 animate-spin" />}
            Use image
          </button>
        </div>
      </div>
    </div>
  )

  // Portalled: an ancestor with backdrop-filter would otherwise become the
  // containing block and this would land inside the panel instead of the page.
  return typeof document === "undefined" ? content : createPortal(content, document.body)
}

function PresetButton({ active, disabled, onClick, Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs transition-colors disabled:opacity-50 ${
        active
          ? "border-purple-500/50 bg-purple-600/20 text-purple-200"
          : "border-white/15 bg-white/5 text-gray-400 hover:text-white"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )
}

/** The biggest square that fits inside the image, centred. */
const fillBox = (img) => {
  const size = Math.min(img.width, img.height)
  return { size, x: (img.width - size) / 2, y: (img.height - size) / 2 }
}

/** The smallest square that contains the whole image, centred. */
const fitBox = (img) => {
  const size = Math.max(img.width, img.height)
  return { size, x: (img.width - size) / 2, y: (img.height - size) / 2 }
}

const cornerStyle = (h) => ({
  cursor: `${h}-resize`,
  top: h[0] === "n" ? -12 : undefined,
  bottom: h[0] === "s" ? -12 : undefined,
  left: h[1] === "w" ? -12 : undefined,
  right: h[1] === "e" ? -12 : undefined,
})

const near = (a, b) => Math.abs(a - b) < 1
